import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";

import { ButtonLink } from "@/components/Interface/Button/Button";
import { StepFlowLayout } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowLayout";
import { LocalLoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import {
  BulkMoveLocationState,
  sortBillingAccounts,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetAccountList } from "@/services/api";

import { BulkMoveForm } from "./BulkMoveForm";

/**
 * Sammelverschiebung: moves the Abos picked in the Vertragskonten list to
 * another Vertragskonto. Only reachable from that list, which hands over the
 * source and the picked Abos as router state.
 */
export const BulkMoveSubscriptions: FC = () => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts.bulkMove",
  });
  const { t: tDefault } = useTranslation();
  const navigate = useNavigate();
  const state = useLocation().state as BulkMoveLocationState | null;
  const { data: accounts, isPending: isLoading } = useGetAccountList({
    expanded: true,
  });

  if (!state) {
    return <Navigate to={portalRoutes.documents.payments} replace />;
  }

  const source = accounts?.find((account) => account.id === state.sourceId);

  if (!isLoading && !source) {
    return <Navigate to={state.returnTo} replace />;
  }

  const goBack = () => navigate(state.returnTo, { replace: true });

  return (
    <StepFlowLayout
      dataTestid="bulk-move"
      title={t("title")}
      description={t("description")}
      headerAction={
        <ButtonLink
          data-testid="bulk-move-cancel"
          variant="tertiary"
          to={state.returnTo}
          replace
        >
          {tDefault("Common.label.cancel")}
        </ButtonLink>
      }
    >
      {source ? (
        <BulkMoveForm
          source={source}
          accounts={sortBillingAccounts(accounts ?? [])}
          initialSimIds={state.simIds}
          onDone={goBack}
        />
      ) : (
        <div className="flex justify-center py-6">
          <LocalLoadingSpinner />
        </div>
      )}
    </StepFlowLayout>
  );
};
